import React, { useState } from "react";
import { CONTENT_TYPES, CONTENT_TYPE_NO_MEDIA, CONTENT_TYPE_IMAGE, getEffectiveLimits } from "../constants";
import OruclicksAIIcon from "../../../Components/AIChat/OruclicksAIIcon";
import MarketingAssetPickerDialog from "./MarketingAssetPickerDialog";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Chip,
  Avatar,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  FormHelperText,
  Divider,
  Checkbox,
  Tooltip,
  ListSubheader,
  InputAdornment,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Add as AddIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AccountCircle as AccountIcon,
  SelectAll as SelectAllIcon,
  ClearAll as ClearAllIcon,
  GridView as TemplateIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

// Helper function to safely get file URL
const getFileURL = (file) => {
  if (!file) return "";
  
  // If file is already a string URL, return it
  if (typeof file === "string") {
    return file;
  }
  
  // If file is a File or Blob object, create object URL
  if (file instanceof File || file instanceof Blob) {
    try {
      return URL.createObjectURL(file);
    } catch (error) {
      console.error("Error creating object URL:", error);
      return "";
    }
  }
  
  // Fallback: return empty string
  console.warn("Invalid file type passed to getFileURL:", file);
  return "";
};

const RunNow = ({
  // State values
  platforms,
  contentType,
  mediaFile,
  postContent,
  selectedAccounts,
  socialMediaAccounts,
  loadingAccounts,

  // Handlers
  onContentTypeChange,
  onPlatformChange,
  onAccountChange,
  onPostContentChange,
  onMediaUpload,
  onRemovePlatform,
  onRemoveAccount,
  onRemoveMediaFile,
  onRemoveAllMediaFiles,

  // Utilities
  getAvailablePlatforms,
  getAvailableAccounts,

  // AI
  onOpenAIChat,
  isBusinessPlan,

  // Template
  onOpenTemplate,

  // Marketing Assets
  onMarketingAssetSelect,
}) => {
  const availablePlatforms = getAvailablePlatforms(contentType);
  const allPlatformsSelected =
    availablePlatforms.length > 0 &&
    availablePlatforms.every((p) => platforms.includes(p));

  const handlePlatformChange = (e) => {
    const value = e.target.value;
    if (Array.isArray(value) && value.includes("__select_all_platforms__")) {
      onPlatformChange({ target: { value: allPlatformsSelected ? [] : availablePlatforms } });
    } else {
      onPlatformChange(e);
    }
  };

  const availableAccounts = getAvailableAccounts();
  const allAccountIds = availableAccounts.map((a) => a.accountId);
  const allSelected =
    allAccountIds.length > 0 &&
    allAccountIds.every((id) => selectedAccounts.includes(id));

  const handleSelectAllAccounts = () => {
    onAccountChange({ target: { value: allSelected ? [] : allAccountIds } });
  };

  const handleAccountChange = (e) => {
    const value = e.target.value;
    if (Array.isArray(value) && value.includes("__select_all__")) {
      onAccountChange({ target: { value: allSelected ? [] : allAccountIds } });
    } else {
      onAccountChange(e);
    }
  };

  // Add validation helper
  const isMediaRequired = contentType && contentType !== "Content Only";
  const hasMedia =
    mediaFile && (Array.isArray(mediaFile) ? mediaFile.length > 0 : true);
  const isMediaMissing = isMediaRequired && !hasMedia;

  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [platformSearch, setPlatformSearch] = useState("");

  const filteredPlatforms = platformSearch.trim()
    ? availablePlatforms.filter((p) =>
        p.toLowerCase().includes(platformSearch.toLowerCase())
      )
    : availablePlatforms;

  const filteredAccounts = accountSearch.trim()
    ? availableAccounts.filter((account) => {
        const q = accountSearch.toLowerCase();
        return (
          account.accountName?.toLowerCase().includes(q) ||
          account.provider?.toLowerCase().includes(q) ||
          account.accountId?.toLowerCase().includes(q)
        );
      })
    : availableAccounts;

  const isUploadDisabled = !contentType || platforms.length === 0 || selectedAccounts.length === 0;
  const currentFileCount = Array.isArray(mediaFile) ? mediaFile.length : (mediaFile ? 1 : 0);
  const maxFileCount = contentType === "Image" ? 10 : 5;
  const atMaxFiles = currentFileCount >= maxFileCount;

  return (
    <Grid container spacing={2}>
      {/* Content Type */}
      <Grid item xs={12}>
        <FormControl fullWidth sx={{ mb: 2 }} error={!contentType}>
          <InputLabel>Content Type *</InputLabel>
          <Select
            required
            value={contentType}
            onChange={(e) => onContentTypeChange(e.target.value)}
            label="Content Type *"
            sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
          >
            {CONTENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </Select>
          {!contentType && (
            <FormHelperText>Please select a content type first</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Platforms */}
      <Grid item xs={12}>
        {/* Quick Select All / Clear All bar for platforms */}
        {availablePlatforms.length > 0 && contentType && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
              px: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500 }}
            >
              {platforms.length} / {availablePlatforms.length} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label="Select All"
                size="small"
                icon={<SelectAllIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() =>
                  onPlatformChange({ target: { value: allPlatformsSelected ? [] : availablePlatforms } })
                }
                disabled={allPlatformsSelected}
                sx={{
                  cursor: "pointer",
                  bgcolor: allPlatformsSelected ? "#dcfce7" : "#e0e7ff",
                  color: allPlatformsSelected ? "#15803d" : "#4B5AB6",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  "&:hover": { bgcolor: "#c7d2fe" },
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              />
              <Chip
                label="Clear All"
                size="small"
                icon={<ClearAllIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() =>
                  onPlatformChange({ target: { value: [] } })
                }
                disabled={platforms.length === 0}
                sx={{
                  cursor: "pointer",
                  bgcolor: "#fef2f2",
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  "&:hover": { bgcolor: "#fee2e2" },
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              />
            </Box>
          </Box>
        )}
        <FormControl
          fullWidth
          sx={{ mb: 2 }}
          error={platforms.length === 0}
          disabled={!contentType}
        >
          <InputLabel>Platforms *</InputLabel>
          <Select
            multiple
            required
            value={platforms}
            onChange={handlePlatformChange}
            label="Platforms *"
            onClose={() => setPlatformSearch("")}
            MenuProps={{ PaperProps: { sx: { maxHeight: 380 } } }}
            renderValue={(selected) => (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "nowrap",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                {selected.slice(0, 2).map((value) => (
                  <Box
                    key={value}
                    sx={{ display: "inline-flex", flexShrink: 0 }}
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                  >
                    <Chip
                      size="small"
                      label={value}
                      onDelete={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onRemovePlatform(value);
                      }}
                      deleteIcon={<CloseIcon sx={{ fontSize: "13px !important", cursor: "pointer" }} />}
                      sx={{
                        bgcolor: "#e0e7ff",
                        color: "#4B5AB6",
                        fontSize: "0.72rem",
                        height: 24,
                        "& .MuiChip-deleteIcon": {
                          color: "#4B5AB6",
                          "&:hover": { color: "#ef4444" },
                        },
                      }}
                    />
                  </Box>
                ))}
                {selected.length > 2 && (
                  <Box sx={{
                    flexShrink: 0,
                    bgcolor: "#4B5AB6",
                    color: "#fff",
                    borderRadius: "12px",
                    px: 0.8,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    lineHeight: "24px",
                    height: 24,
                  }}>
                    +{selected.length - 2}
                  </Box>
                )}
              </Box>
            )}
            sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
          >
            {availablePlatforms.length > 0 && [
              <ListSubheader key="__platform_search_box__" sx={{ p: 1, lineHeight: "normal", bgcolor: "#fff", zIndex: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search platforms…"
                  value={platformSearch}
                  onChange={(e) => setPlatformSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}
                />
              </ListSubheader>,
              <MenuItem key="__select_all_platforms__" value="__select_all_platforms__" sx={{ borderBottom: "1px solid #e2e8f0", mb: 0.5 }}>
                <Checkbox
                  checked={allPlatformsSelected}
                  indeterminate={platforms.length > 0 && !allPlatformsSelected}
                  sx={{
                    color: "#4B5AB6",
                    "&.Mui-checked": { color: "#4B5AB6" },
                    "&.MuiCheckbox-indeterminate": { color: "#4B5AB6" },
                    p: 0.5, mr: 1,
                  }}
                />
                <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.875rem" }}>
                  {allPlatformsSelected ? "Deselect All" : `Select All (${availablePlatforms.length})`}
                </Typography>
              </MenuItem>,
              ...(filteredPlatforms.length === 0
                ? [<MenuItem key="__no_platform_match__" disabled><Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", fontStyle: "italic" }}>No platforms match "{platformSearch}"</Typography></MenuItem>]
                : filteredPlatforms.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  <Checkbox
                    checked={platforms.includes(platform)}
                    sx={{
                      color: "#4B5AB6",
                      "&.Mui-checked": { color: "#4B5AB6" },
                      p: 0.5, mr: 1,
                    }}
                  />
                  {platform}
                </MenuItem>
              ))),
            ]}
          </Select>
          {platforms.length === 0 && (
            <FormHelperText>
              {!contentType
                ? "Please select content type first"
                : "Please select at least one platform"}
            </FormHelperText>
          )}
          {contentType && availablePlatforms.length > 0 && (
            <FormHelperText sx={{ color: "#4B5AB6", fontWeight: 500 }}>
              Available for {contentType}: {availablePlatforms.join(", ")}
            </FormHelperText>
          )}
          {platforms.length > 0 && (
            <FormHelperText sx={{ color: "#059669", fontWeight: 500 }}>
              💡 Click the ✕ icon on any chip to remove it quickly
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Social Media Accounts */}
      <Grid item xs={12}>
        {/* Quick Select All / Clear All bar */}
        {availableAccounts.length > 0 && !loadingAccounts && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.75,
              px: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 500 }}
            >
              {selectedAccounts.length} / {availableAccounts.length} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label="Select All"
                size="small"
                icon={<SelectAllIcon sx={{ fontSize: "14px !important" }} />}
                onClick={handleSelectAllAccounts}
                disabled={allSelected || platforms.length === 0 || !contentType}
                sx={{
                  cursor: "pointer",
                  bgcolor: allSelected ? "#dcfce7" : "#e0f2fe",
                  color: allSelected ? "#15803d" : "#0277bd",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  "&:hover": { bgcolor: "#bae6fd" },
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              />
              <Chip
                label="Clear All"
                size="small"
                icon={<ClearAllIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() =>
                  onAccountChange({ target: { value: [] } })
                }
                disabled={selectedAccounts.length === 0}
                sx={{
                  cursor: "pointer",
                  bgcolor: "#fef2f2",
                  color: "#dc2626",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  "&:hover": { bgcolor: "#fee2e2" },
                  "&.Mui-disabled": { opacity: 0.4 },
                }}
              />
            </Box>
          </Box>
        )}
        <FormControl
          fullWidth
          sx={{ mb: 2 }}
          error={selectedAccounts.length === 0}
          disabled={platforms.length === 0 || loadingAccounts || !contentType}
        >
          <InputLabel>Select Social Media Accounts *</InputLabel>
          <Select
            multiple
            required
            value={selectedAccounts}
            onChange={handleAccountChange}
            label="Select Social Media Accounts *"
            onClose={() => setAccountSearch("")}
            MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
            renderValue={(selected) => (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  minHeight: "40px",
                  alignItems: "center",
                }}
              >
                {selected.length >= 4 ? (
                  /* Compact summary badge for 4+ accounts */
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={
                        allSelected
                          ? `All ${selected.length} accounts selected`
                          : `${selected.length} accounts selected`
                      }
                      sx={{
                        bgcolor: "#e8f5e8",
                        color: "#2e7d32",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                      }}
                      avatar={<SelectAllIcon sx={{ color: "#2e7d32 !important" }} />}
                    />
                    <Chip
                      label="Clear all"
                      size="small"
                      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAccountChange({ target: { value: [] } });
                      }}
                      sx={{
                        bgcolor: "#fef2f2",
                        color: "#dc2626",
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#fee2e2" },
                      }}
                      deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "#dc2626 !important" }} />}
                      onDelete={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAccountChange({ target: { value: [] } });
                      }}
                    />
                  </Box>
                ) : (
                  selected.map((accountId) => {
                    const account = socialMediaAccounts.find(
                      (acc) => acc.accountId === accountId
                    );
                    return (
                      <Box
                        key={accountId}
                        sx={{ position: "relative", display: "inline-flex" }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <Chip
                          label={`${account?.accountName || accountId} (${
                            account?.provider === "Twitter"
                              ? "Twitter (X)"
                              : account?.provider || "Unknown"
                          })`}
                          sx={{
                            bgcolor: "#e8f5e8",
                            color: "#2e7d32",
                            "& .MuiChip-deleteIcon": {
                              color: "#2e7d32",
                              "&:hover": { color: "#ef4444" },
                            },
                          }}
                          avatar={<AccountIcon />}
                          onDelete={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onRemoveAccount(accountId);
                          }}
                          deleteIcon={
                            <CloseIcon
                              sx={{
                                fontSize: "16px !important",
                                cursor: "pointer",
                              }}
                            />
                          }
                        />
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
            sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
          >
            {loadingAccounts ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                Loading accounts...
              </MenuItem>
            ) : availableAccounts.length === 0 ? (
              <MenuItem disabled>
                {!contentType
                  ? "Please select content type first"
                  : platforms.length === 0
                  ? "Please select platforms first"
                  : "No accounts available for selected platforms"}
              </MenuItem>
            ) : (
              [
                <ListSubheader key="__search_box__" sx={{ p: 1, lineHeight: "normal", bgcolor: "#fff", zIndex: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by name, platform, ID…"
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}
                  />
                </ListSubheader>,
                <MenuItem key="__select_all__" value="__select_all__" sx={{ borderBottom: "1px solid #e2e8f0", mb: 0.5 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedAccounts.length > 0 && !allSelected}
                    sx={{
                      color: "#4B5AB6",
                      "&.Mui-checked": { color: "#4B5AB6" },
                      "&.MuiCheckbox-indeterminate": { color: "#4B5AB6" },
                      p: 0.5, mr: 1,
                    }}
                  />
                  <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "0.875rem" }}>
                    {allSelected ? "Deselect All" : `Select All (${allAccountIds.length})`}
                  </Typography>
                </MenuItem>,
                ...(filteredAccounts.length === 0
                  ? [<MenuItem key="__no_match__" disabled><Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", fontStyle: "italic" }}>No accounts match "{accountSearch}"</Typography></MenuItem>]
                  : filteredAccounts.map((account) => (
                <MenuItem key={account.accountId} value={account.accountId}>
                  <Checkbox
                    checked={selectedAccounts.includes(account.accountId)}
                    sx={{
                      color: "#4B5AB6",
                      "&.Mui-checked": { color: "#4B5AB6" },
                      p: 0.5, mr: 1, flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#4B5AB6" }}>
                      {account.provider.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {account.accountName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {account.provider === "Twitter"
                          ? "Twitter (X)"
                          : account.provider}{" "}
                        • {account.accountId} • {account.status}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        account.provider === "Twitter"
                          ? "Twitter (X)"
                          : account.provider
                      }
                      size="small"
                      sx={{
                        bgcolor: "#e0e7ff",
                        color: "#4B5AB6",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </MenuItem>
              )))
            ])}
          </Select>
          {selectedAccounts.length === 0 && (
            <FormHelperText>
              {!contentType
                ? "Please select content type first"
                : platforms.length === 0
                ? "Please select platforms first"
                : "Please select at least one social media account"}
            </FormHelperText>
          )}
          {selectedAccounts.length > 0 && (
            <FormHelperText sx={{ color: "#059669", fontWeight: 500 }}>
              💡 Click the ✕ icon on any account chip to remove it quickly
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Post Content */}
      <Grid item xs={12}>
        <TextField
          label="Post Content *"
          required
          multiline
          rows={8}
          fullWidth
          value={postContent}
          onChange={(e) => onPostContentChange(e.target.value)}
          error={!postContent.trim()}
          helperText={!postContent.trim() ? "Post content is required" : ""}
          disabled={
            !contentType ||
            platforms.length === 0 ||
            selectedAccounts.length === 0
          }
          sx={{
            bgcolor: "#f8fafc",
            borderRadius: 2,
            fontSize: "1.1rem",
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
        {/* AI + Template button row below textarea */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Tooltip
            title={
              isBusinessPlan
                ? "Generate content with AI"
                : "Upgrade to Business plan to unlock AI content generation"
            }
            arrow
            placement="top"
          >
            <Box
              onClick={isBusinessPlan ? onOpenAIChat : undefined}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.9,
                borderRadius: "10px",
                cursor: isBusinessPlan ? "pointer" : "not-allowed",
                background: isBusinessPlan
                  ? "linear-gradient(135deg, #0f172a 0%, #1e1b6e 50%, #065f46 100%)"
                  : "#e2e8f0",
                border: isBusinessPlan
                  ? "1px solid rgba(16,185,129,0.5)"
                  : "1px solid #cbd5e1",
                boxShadow: isBusinessPlan
                  ? "0 2px 10px rgba(99,102,241,0.25)"
                  : "none",
                opacity: isBusinessPlan ? 1 : 0.6,
                transition: "all 0.2s ease",
                userSelect: "none",
                "&:hover": isBusinessPlan
                  ? {
                      opacity: 0.88,
                      boxShadow: "0 4px 16px rgba(99,102,241,0.4)",
                      transform: "translateY(-1px)",
                    }
                  : {},
              }}
            >
              <OruclicksAIIcon size={18} />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: isBusinessPlan ? "#a5f3fc" : "#94a3b8",
                  letterSpacing: "0.03em",
                }}
              >
                Generate with AI
              </Typography>
            </Box>
          </Tooltip>

          {onOpenTemplate && (
            <Button
              variant="outlined"
              startIcon={<TemplateIcon sx={{ fontSize: "18px !important" }} />}
              onClick={onOpenTemplate}
              sx={{
                px: 2,
                py: 0.85,
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "#4B5AB6",
                borderColor: "#c7d2fe",
                bgcolor: "#f5f3ff",
                letterSpacing: "0.03em",
                textTransform: "none",
                boxShadow: "0 1px 4px rgba(99,102,241,0.12)",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#eef2ff",
                  borderColor: "#818cf8",
                  boxShadow: "0 3px 10px rgba(99,102,241,0.2)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Choose Template
            </Button>
          )}
        </Box>
      </Grid>

      {/* Media Upload Section */}
      {contentType && contentType !== "Content Only" && (
        <Grid item xs={12}>
          {!mediaFile ||
          (Array.isArray(mediaFile) && mediaFile.length === 0) ? (
            <>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                {/* Upload from Local */}
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon sx={{ fontSize: "18px !important" }} />}
                  disabled={isUploadDisabled}
                  sx={{
                    px: 2,
                    py: 0.85,
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: isMediaMissing ? "#ef4444" : "#4B5AB6",
                    borderColor: isMediaMissing ? "#fca5a5" : "#c7d2fe",
                    bgcolor: isMediaMissing ? "#fff1f2" : "#f5f3ff",
                    letterSpacing: "0.03em",
                    textTransform: "none",
                    boxShadow: isMediaMissing
                      ? "0 1px 4px rgba(239,68,68,0.12)"
                      : "0 1px 4px rgba(99,102,241,0.12)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: isMediaMissing ? "#fef2f2" : "#eef2ff",
                      borderColor: isMediaMissing ? "#f87171" : "#818cf8",
                      boxShadow: isMediaMissing
                        ? "0 3px 10px rgba(239,68,68,0.2)"
                        : "0 3px 10px rgba(99,102,241,0.2)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": { bgcolor: "#f1f5f9", color: "#94a3b8", borderColor: "#e2e8f0", boxShadow: "none" },
                  }}
                >
                  Upload from Local
                  <input
                    type="file"
                    accept={contentType === "Image" ? "image/*" : "video/*"}
                    multiple
                    hidden
                    onChange={onMediaUpload}
                  />
                </Button>

                {/* Choose from Marketing Assets */}
                <Button
                  variant="outlined"
                  startIcon={<PhotoLibraryIcon sx={{ fontSize: "18px !important" }} />}
                  disabled={isUploadDisabled}
                  onClick={() => setAssetPickerOpen(true)}
                  sx={{
                    px: 2,
                    py: 0.85,
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "#059669",
                    borderColor: "#a7f3d0",
                    bgcolor: "#f0fdf4",
                    letterSpacing: "0.03em",
                    textTransform: "none",
                    boxShadow: "0 1px 4px rgba(5,150,105,0.12)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#dcfce7",
                      borderColor: "#34d399",
                      boxShadow: "0 3px 10px rgba(5,150,105,0.2)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": { bgcolor: "#f1f5f9", color: "#94a3b8", borderColor: "#e2e8f0", boxShadow: "none" },
                  }}
                >
                  Choose from Marketing Assets
                </Button>
              </Box>

              {/* Error message when media is required but not uploaded */}
              {isMediaMissing && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "#fef2f2",
                    borderRadius: 2,
                    border: "1px solid #ef4444",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#dc2626", fontWeight: 600 }}
                  >
                    ⚠️ {contentType} Required
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#991b1b", display: "block", mt: 0.5 }}
                  >
                    Please upload at least one {contentType.toLowerCase()} file
                    to continue.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Paper
              sx={{
                p: 3,
                bgcolor: "#f8fafc",
                borderRadius: 3,
                border: "2px dashed #d1d5db",
                position: "relative",
              }}
            >
              {/* Header with file count and buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#1e293b" }}
                >
                  {contentType} Files (
                  {Array.isArray(mediaFile) ? mediaFile.length : 1})
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {/* Add from Local */}
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={atMaxFiles}
                    sx={{
                      color: "#4B5AB6",
                      borderColor: "#4B5AB6",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#eef2ff" },
                    }}
                  >
                    Add from Local
                    <input
                      type="file"
                      accept={contentType === "Image" ? "image/*" : "video/*"}
                      multiple
                      hidden
                      onChange={onMediaUpload}
                    />
                  </Button>

                  {/* Add from Marketing Assets */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PhotoLibraryIcon />}
                    disabled={atMaxFiles}
                    onClick={() => setAssetPickerOpen(true)}
                    sx={{
                      color: "#059669",
                      borderColor: "#059669",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#f0fdf4" },
                    }}
                  >
                    Add from Assets
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onRemoveAllMediaFiles}
                    sx={{
                      color: "#ef4444",
                      borderColor: "#ef4444",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "#fef2f2",
                        borderColor: "#dc2626",
                        color: "#dc2626",
                      },
                    }}
                  >
                    Remove All
                  </Button>
                </Box>
              </Box>

              {/* File Grid */}
              <Grid container spacing={2}>
                {(Array.isArray(mediaFile) ? mediaFile : [mediaFile]).map(
                  (file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: "#fff",
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          position: "relative",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <IconButton
                          onClick={() => onRemoveMediaFile(index)}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "#ef4444",
                            color: "#fff",
                            zIndex: 1,
                            width: 24,
                            height: 24,
                            "&:hover": { bgcolor: "#dc2626" },
                          }}
                          size="small"
                          title="Remove this file"
                        >
                          ✕
                        </IconButton>

                        <Box sx={{ mb: 2 }}>
                          {contentType === "Image" ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: 120,
                                borderRadius: 2,
                                overflow: "hidden",
                                bgcolor: "#e5e7eb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={file.fromMarketingAsset ? file.url : getFileURL(file)}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                borderRadius: 2,
                                overflow: "hidden",
                                bgcolor: "#e5e7eb",
                              }}
                            >
                              <video
                                src={file.fromMarketingAsset ? file.url : getFileURL(file)}
                                controls
                                style={{
                                  width: "100%",
                                  maxHeight: "120px",
                                  borderRadius: "8px",
                                }}
                              />
                            </Box>
                          )}
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#1e293b",
                              wordBreak: "break-word",
                              fontSize: "0.8rem",
                              mb: 1,
                            }}
                            title={`Original: ${
                              file.originalName || file.name
                            }\nUnique: ${file.uniqueName || file.name}`}
                          >
                            {(file.originalName || file.name).length > 20
                              ? `${(file.originalName || file.name).substring(
                                  0,
                                  20
                                )}...`
                              : file.originalName || file.name}
                          </Typography>

                          {file.fromMarketingAsset ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#4B5AB6",
                                fontSize: "0.65rem",
                                display: "block",
                                fontStyle: "italic",
                                mb: 0.5,
                              }}
                            >
                              ✦ From Marketing Assets
                            </Typography>
                          ) : file.uniqueName ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#059669",
                                fontSize: "0.65rem",
                                display: "block",
                                fontStyle: "italic",
                                mb: 0.5,
                              }}
                              title={`Unique filename: ${file.uniqueName}`}
                            >
                              ✓ Unique name generated
                            </Typography>
                          ) : null}

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#6b7280" }}
                            >
                              {(file.size / 1024 / 1024).toFixed(1)} MB
                            </Typography>
                            <Chip
                              label={file.type.split("/")[1].toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor: "#dbeafe",
                                color: "#1e40af",
                                fontSize: "0.6rem",
                                height: 20,
                              }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  )
                )}
              </Grid>

              {/* Upload Limits Info */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "#fffbeb",
                  borderRadius: 2,
                  border: "1px solid #fbbf24",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#92400e", fontWeight: 600 }}
                >
                  Upload Limits:
                </Typography>
                <Typography variant="caption" sx={{ color: "#92400e", ml: 1 }}>
                  {contentType === "Image"
                    ? `${
                        Array.isArray(mediaFile) ? mediaFile.length : 1
                      }/10 images • Max 10MB each`
                    : `${
                        Array.isArray(mediaFile) ? mediaFile.length : 1
                      }/5 videos • Max 50MB each`}
                </Typography>
              </Box>
            </Paper>
          )}

          {contentType && (
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                mt: 1,
                display: "block",
                fontStyle: "italic",
              }}
            >
              Supported formats:{" "}
              {contentType === "Image"
                ? "JPG, PNG, GIF, WebP • Max: 10 files, 10MB each"
                : "MP4, MOV, AVI, WebM • Max: 5 files, 50MB each"}
            </Typography>
          )}
        </Grid>
      )}

      {/* Add validation summary at the bottom */}
      {(isMediaMissing ||
        !postContent.trim() ||
        platforms.length === 0 ||
        selectedAccounts.length === 0) && (
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 2,
              mb: 1,
              px: 1.5,
              py: 1,
              bgcolor: "#fff7ed",
              borderRadius: 2,
              border: "1px solid #fb923c",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#c2410c", mr: 0.5, whiteSpace: "nowrap" }}>
              📋 To post:
            </Typography>
            {[
              { label: "Content Type", done: !!contentType },
              { label: "Platform", done: platforms.length > 0 },
              { label: "Account", done: selectedAccounts.length > 0 },
              { label: "Content", done: !!postContent.trim() },
              ...(isMediaRequired ? [{ label: contentType, done: hasMedia }] : []),
            ].map(({ label, done }) => (
              <Box
                key={label}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  px: 0.9,
                  py: 0.25,
                  borderRadius: "20px",
                  bgcolor: done ? "rgba(5,150,105,0.1)" : "rgba(220,38,38,0.08)",
                  border: `1px solid ${done ? "#6ee7b7" : "#fca5a5"}`,
                }}
              >
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: done ? "#059669" : "#dc2626", lineHeight: 1 }}>
                  {done ? "✓" : "✗"}
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", color: done ? "#065f46" : "#991b1b", lineHeight: 1 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      )}
      <MarketingAssetPickerDialog
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={(selected) => {
          setAssetPickerOpen(false);
          if (onMarketingAssetSelect) onMarketingAssetSelect(selected);
        }}
        contentType={contentType}
      />
    </Grid>
  );
};

export default RunNow;
